import { useApp } from '@/components/AppLayout';
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useEffect, useState, useCallback, useRef } from 'react';
import {
  dashboardApi,
  type DashboardChartData,
  type DashboardChartDataPoint,
  type DashboardTopClient,
  type DashboardKeyStats,
  type DashboardActionCards,
} from '@/lib/api';
import { formatAmount, formatAmountWithSmallCents, formatAmountNoCents } from '@/lib/currency';
import { Area, AreaChart, XAxis } from 'recharts';
import { ArrowUpRight, Calendar, Receipt, Users, ChevronRight, Sparkles } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import { WelcomeUpgradeModal } from '@/components/WelcomeUpgradeModal';
import { UltimateOwlUpsellModal } from '@/components/Modals/UltimateOwlUpsellModal';
import { useUpgradeModal } from '@/contexts/UpgradeModalContext';
import { cn } from '@/lib/utils';
import { SetupProgress } from '@/components/SetupProgress';
import { QuickCreateMenu } from '@/components/QuickCreateMenu';
import { OnboardingChecklist } from '@/components/OnboardingChecklist';

type TimeRange = '7' | '30' | '365' | 'all';

const TIME_RANGE_LABELS: Record<TimeRange, string> = {
  '7': 'Last 7 days',
  '30': 'Last 30 days',
  '365': 'Last 365 days',
  'all': 'All time',
};

function formatDateLabel(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function TimeRangeSelect({
  value,
  onChange,
}: {
  value: TimeRange;
  onChange: (value: TimeRange) => void;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-auto py-0 text-sm text-muted-foreground border-0 shadow-none bg-transparent hover:text-foreground w-auto gap-1 px-0">
        <SelectValue />
      </SelectTrigger>
      <SelectContent align="start">
        <SelectItem value="7">{TIME_RANGE_LABELS['7']}</SelectItem>
        <SelectItem value="30">{TIME_RANGE_LABELS['30']}</SelectItem>
        <SelectItem value="365">{TIME_RANGE_LABELS['365']}</SelectItem>
        <SelectItem value="all">{TIME_RANGE_LABELS['all']}</SelectItem>
      </SelectContent>
    </Select>
  );
}

const revenueChartConfig = {
  amount: {
    label: 'Revenue',
    color: 'hsl(142 71% 45%)',
  },
} satisfies ChartConfig;

const paymentsChartConfig = {
  amount: {
    label: 'Payments',
    color: 'hsl(221 83% 53%)',
  },
} satisfies ChartConfig;

// Animated currency display - instant update on hover
function AnimatedCurrency({
  value,
  loading,
}: {
  value: number;
  loading: boolean;
}) {
  const { dollars, cents } = formatAmountWithSmallCents(loading ? 0 : value);

  if (loading) {
    return <div className="h-9 w-32 bg-muted animate-pulse rounded" />;
  }

  return (
    <div className="tabular-nums">
      <span className="text-3xl font-semibold tracking-tight">{dollars}</span>
      <span className="text-lg text-muted-foreground">{cents}</span>
    </div>
  );
}

function ChartCard({
  title,
  data,
  timeRange,
  onTimeRangeChange,
  loading,
  chartConfig,
  accentColor,
}: {
  title: string;
  data: DashboardChartDataPoint[];
  timeRange: TimeRange;
  onTimeRangeChange: (value: TimeRange) => void;
  loading: boolean;
  chartConfig: ChartConfig;
  accentColor: string;
}) {
  const [hoveredValue, setHoveredValue] = useState<number | null>(null);

  const total = data.length > 0 ? data[data.length - 1].amount : 0;
  const displayValue = hoveredValue !== null ? hoveredValue : total;

  const handleMouseMove = useCallback((state: { activePayload?: Array<{ payload: DashboardChartDataPoint }> }) => {
    if (state.activePayload?.length && state.activePayload.length > 0) {
      setHoveredValue(state.activePayload[0].payload.amount);
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoveredValue(null);
  }, []);

  return (
    <div className="bg-card rounded-xl border shadow-sm overflow-hidden relative">
      <div className={cn('absolute top-0 right-0 w-48 h-48 rounded-full blur-3xl opacity-10 -translate-y-1/2 translate-x-1/2 pointer-events-none', accentColor)} />
      <div className="p-5 pb-4 relative">
        <div className="flex items-center justify-between mb-1">
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <TimeRangeSelect value={timeRange} onChange={onTimeRangeChange} />
        </div>
        <AnimatedCurrency value={displayValue} loading={loading} />
      </div>
      <div className="px-5 pb-5 relative">
        {loading ? (
          <div className="h-[140px]" />
        ) : data.length === 0 ? (
          <div className="h-[140px] flex items-center justify-center text-muted-foreground text-sm">
            No data for this period
          </div>
        ) : (
          <ChartContainer config={chartConfig} className="h-[140px] w-full">
            <AreaChart
              data={data}
              margin={{ top: 5, right: 0, left: 0, bottom: 0 }}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
            >
              <defs>
                <linearGradient id={`fill-${title.replace(/\s/g, '')}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--color-amount)" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="var(--color-amount)" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="date"
                tickFormatter={formatDateLabel}
                axisLine={false}
                tickLine={false}
                tickMargin={8}
                minTickGap={60}
                fontSize={12}
              />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    labelFormatter={formatDateLabel}
                    formatter={(value) => formatAmount(value as number)}
                  />
                }
              />
              <Area
                type="monotone"
                dataKey="amount"
                stroke="var(--color-amount)"
                strokeWidth={2.5}
                fill={`url(#fill-${title.replace(/\s/g, '')})`}
              />
            </AreaChart>
          </ChartContainer>
        )}
      </div>
    </div>
  );
}

function KeyStatsCard({
  stats,
  timeRange,
  onTimeRangeChange,
  loading,
  isViewOnly,
}: {
  stats: DashboardKeyStats | null;
  timeRange: TimeRange;
  onTimeRangeChange: (value: TimeRange) => void;
  loading: boolean;
  isViewOnly?: boolean;
}) {
  const { nav } = useBusinessNav();
  const formatStatValue = (value: number) => {
    const { dollars, cents } = formatAmountWithSmallCents(value);
    return (
      <>
        <span>{dollars}</span>
        <span className="text-muted-foreground">{cents}</span>
      </>
    );
  };

  return (
    <div className="bg-card rounded-xl border shadow-sm overflow-hidden flex flex-col">
      <div className="p-5 pb-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-muted-foreground">Key Stats</p>
          <TimeRangeSelect value={timeRange} onChange={onTimeRangeChange} />
        </div>
      </div>
      <div className="px-5 pb-5 flex-1">
        {loading ? (
          <div className="h-[120px]" />
        ) : stats ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-blue-100 dark:bg-blue-950 flex items-center justify-center">
                <Calendar className="h-4 w-4 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-muted-foreground">Avg. time to get paid</p>
                <p className="text-sm font-medium">
                  {stats.avg_days_to_paid !== null ? `${stats.avg_days_to_paid} days` : '—'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-emerald-100 dark:bg-emerald-950 flex items-center justify-center">
                <Receipt className="h-4 w-4 text-emerald-600" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-muted-foreground">Avg. invoice size</p>
                <p className="text-sm font-medium">{formatStatValue(stats.avg_invoice_size)}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-violet-100 dark:bg-violet-950 flex items-center justify-center">
                <Users className="h-4 w-4 text-violet-600" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-muted-foreground">Clients invoiced</p>
                <p className="text-sm font-medium">{stats.clients_invoiced}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="h-[120px] flex items-center justify-center text-muted-foreground text-sm">
            No data available
          </div>
        )}
      </div>
      {!isViewOnly && (
        <button
          onClick={() => nav('/settings/reminders')}
          className="border-t bg-gradient-to-br from-violet-50 to-pink-50 dark:from-violet-950/50 dark:to-pink-950/50 px-5 py-4 hover:from-violet-100 hover:to-pink-100 dark:hover:from-violet-950 dark:hover:to-pink-950 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-medium">Automated reminders</p>
              <p className="text-xs text-muted-foreground">Upgrade to access this feature</p>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
        </button>
      )}
    </div>
  );
}

function TopClientsCard({
  clients,
  timeRange,
  onTimeRangeChange,
  loading,
}: {
  clients: DashboardTopClient[];
  timeRange: TimeRange;
  onTimeRangeChange: (value: TimeRange) => void;
  loading: boolean;
}) {
  const { getPath } = useBusinessNav();
  const avatarColors = [
    'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400',
    'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400',
    'bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-400',
    'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400',
    'bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-400',
  ];

  return (
    <div className="bg-card rounded-xl border shadow-sm overflow-hidden flex flex-col">
      <div className="p-5 pb-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-muted-foreground">Top Clients</p>
          <TimeRangeSelect value={timeRange} onChange={onTimeRangeChange} />
        </div>
      </div>
      <div className="px-5 flex-1">
        {loading ? (
          <div className="h-[180px]" />
        ) : clients.length === 0 ? (
          <div className="h-[180px] flex items-center justify-center text-muted-foreground text-sm">
            No clients for this period
          </div>
        ) : (
          <div className="space-y-3">
            {clients.slice(0, 4).map((client, index) => (
              <Link
                key={client.id}
                to={getPath(`/clients/${client.id}`)}
                className="flex items-center justify-between hover:bg-muted/50 -mx-2 px-2 py-2 rounded-lg transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div
                    className={cn('h-10 w-10 rounded-xl flex items-center justify-center text-xs font-semibold', avatarColors[index % avatarColors.length])}
                  >
                    {getInitials(client.name)}
                  </div>
                  <span className="text-sm font-medium">{client.name}</span>
                </div>
                <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">{formatAmountNoCents(client.total_revenue)}</span>
              </Link>
            ))}
          </div>
        )}
      </div>
      <Link
        to={getPath('/clients')}
        className="flex items-center justify-center gap-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 py-3 border-t transition-colors mt-auto"
      >
        <ArrowUpRight className="h-4 w-4" />
        View all clients
      </Link>
    </div>
  );
}

function ActionCard({
  title,
  count,
  amount,
  href,
  loading,
}: {
  title: string;
  count: number;
  amount: number;
  href: string;
  loading?: boolean;
}) {
  const { getPath } = useBusinessNav();

  return (
    <Link
      to={getPath(href)}
      className="group block bg-card rounded-xl border p-6 transition-all hover:border-foreground/20 hover:shadow-sm"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          {loading ? (
            <div className="h-12 w-16 bg-muted animate-pulse rounded mt-2" />
          ) : (
            <p className="text-5xl font-bold tracking-tighter tabular-nums mt-2">
              {count}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="text-xs text-muted-foreground">View</span>
          <ChevronRight className="h-3 w-3 text-muted-foreground" />
        </div>
      </div>
      {!loading && (
        <div className="mt-4 pt-4 border-t">
          <p className="text-sm">
            <span className="font-semibold">{formatAmountNoCents(amount)}</span>
            <span className="text-muted-foreground ml-1">total value</span>
          </p>
        </div>
      )}
    </Link>
  );
}

function DashboardContent({
  onShowWelcomeModal,
  onShowUpgradeModal,
  onShowUltimateOwlModal,
  onShowTrialExpiredModal,
  onShowPaymentFailedModal,
  onShowSubscriptionCancelledModal,
}: {
  onShowWelcomeModal?: () => void;
  onShowUpgradeModal?: (limitType: string) => void;
  onShowUltimateOwlModal?: () => void;
  onShowTrialExpiredModal?: () => void;
  onShowPaymentFailedModal?: () => void;
  onShowSubscriptionCancelledModal?: () => void;
}) {
  const { user, currentBusiness, featureFlags, billingStatus, isViewOnly, refreshUser } = useApp();

  // Refresh user/business data when dashboard mounts (e.g. after navigating back)
  // so onboarding checklist state updates after completing a step
  useEffect(() => {
    refreshUser();
  }, []);
  const [revenueRange, setRevenueRange] = useState<TimeRange>('30');
  const [paymentsRange, setPaymentsRange] = useState<TimeRange>('30');
  const [statsRange, setStatsRange] = useState<TimeRange>('30');
  const [clientsRange, setClientsRange] = useState<TimeRange>('30');

  const [revenueData, setRevenueData] = useState<DashboardChartDataPoint[]>([]);
  const [paymentsData, setPaymentsData] = useState<DashboardChartDataPoint[]>([]);
  const [keyStats, setKeyStats] = useState<DashboardKeyStats | null>(null);
  const [topClients, setTopClients] = useState<DashboardTopClient[]>([]);

  const [revenueLoading, setRevenueLoading] = useState(true);
  const [paymentsLoading, setPaymentsLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [clientsLoading, setClientsLoading] = useState(true);

  const [actionCards, setActionCards] = useState<DashboardActionCards | null>(null);
  const [actionCardsLoading, setActionCardsLoading] = useState(true);

  const initialLoadDone = useRef(false);

  useEffect(() => {
    if (!initialLoadDone.current && !actionCardsLoading) {
      initialLoadDone.current = true;
    }
  }, [actionCardsLoading]);

  const fetchData = useCallback(
    async (
      range: TimeRange,
      setData: (data: DashboardChartData) => void,
      setLoading: (loading: boolean) => void
    ) => {
      if (!currentBusiness?.id) return;

      setLoading(true);
      const days = range === 'all' ? undefined : parseInt(range, 10);
      const response = await dashboardApi.getChartData(currentBusiness.id, days);
      if (response.data) {
        setData(response.data);
      } else if (response.error) {
        console.error('Dashboard chart data error:', response.error);
      }
      setLoading(false);
    },
    [currentBusiness?.id]
  );

  useEffect(() => {
    fetchData(
      revenueRange,
      (data) => setRevenueData(data.cumulative_revenue),
      setRevenueLoading
    );
  }, [revenueRange, fetchData]);

  useEffect(() => {
    fetchData(
      paymentsRange,
      (data) => setPaymentsData(data.cumulative_payments),
      setPaymentsLoading
    );
  }, [paymentsRange, fetchData]);

  useEffect(() => {
    fetchData(statsRange, (data) => setKeyStats(data.key_stats), setStatsLoading);
  }, [statsRange, fetchData]);

  useEffect(() => {
    fetchData(clientsRange, (data) => setTopClients(data.top_clients), setClientsLoading);
  }, [clientsRange, fetchData]);

  useEffect(() => {
    const fetchActionCards = async () => {
      if (!currentBusiness?.id) return;
      setActionCardsLoading(true);
      const response = await dashboardApi.getChartData(currentBusiness.id);
      if (response.data?.action_cards) {
        setActionCards(response.data.action_cards);
      }
      setActionCardsLoading(false);
    };
    fetchActionCards();
  }, [currentBusiness?.id]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  // Determine if user has sent at least one invoice
  const hasSentInvoice = (keyStats?.clients_invoiced ?? 0) > 0;

  return (
    <div className="px-6 pt-6 pb-6">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {getGreeting()}, {user?.first_name}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Here's what's happening with your business</p>
        </div>
        <div className="flex items-center gap-2 mt-1">
          {/* Dev testing buttons - only shown when VITE_DEV_MODE=true */}
          {import.meta.env.VITE_DEV_MODE === 'true' && (
            <div className="flex items-center gap-1 flex-wrap">
              <button
                onClick={onShowWelcomeModal}
                className="text-xs px-2 py-1 bg-pink-500 text-white rounded hover:bg-pink-600"
              >
                Welcome
              </button>
              <button
                onClick={() => onShowUpgradeModal?.('max_invoices')}
                className="text-xs px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Invoices
              </button>
              <button
                onClick={() => onShowUpgradeModal?.('max_quotes')}
                className="text-xs px-2 py-1 bg-purple-500 text-white rounded hover:bg-purple-600"
              >
                Quotes
              </button>
              <button
                onClick={() => onShowUpgradeModal?.('max_businesses')}
                className="text-xs px-2 py-1 bg-emerald-500 text-white rounded hover:bg-emerald-600"
              >
                Businesses
              </button>
              <button
                onClick={() => onShowUpgradeModal?.('max_seats')}
                className="text-xs px-2 py-1 bg-orange-500 text-white rounded hover:bg-orange-600"
              >
                Seats
              </button>
              <button
                onClick={onShowUltimateOwlModal}
                className="text-xs px-2 py-1 bg-amber-500 text-white rounded hover:bg-amber-600"
              >
                Ultimate
              </button>
              <button
                onClick={onShowTrialExpiredModal}
                className="text-xs px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Expired
              </button>
              <button
                onClick={onShowPaymentFailedModal}
                className="text-xs px-2 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600"
              >
                Pay Failed
              </button>
              <button
                onClick={onShowSubscriptionCancelledModal}
                className="text-xs px-2 py-1 bg-zinc-500 text-white rounded hover:bg-zinc-600"
              >
                Cancelled
              </button>
            </div>
          )}
          <SetupProgress
            featureFlags={featureFlags}
            billingStatus={billingStatus}
            hasSentInvoice={hasSentInvoice}
          />
          <QuickCreateMenu />
        </div>
      </div>

      <OnboardingChecklist business={currentBusiness} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <ActionCard
          title="Overdue Invoices"
          count={actionCards?.overdue_invoices.count ?? 0}
          amount={actionCards?.overdue_invoices.amount ?? 0}
          href="/invoices?status=overdue"
          loading={actionCardsLoading}
        />
        <ActionCard
          title="Accepted Quotes"
          count={actionCards?.accepted_quotes.count ?? 0}
          amount={actionCards?.accepted_quotes.amount ?? 0}
          href="/quotes?status=approved"
          loading={actionCardsLoading}
        />
        <ActionCard
          title="Changes Requested"
          count={actionCards?.changes_requested_quotes.count ?? 0}
          amount={actionCards?.changes_requested_quotes.amount ?? 0}
          href="/quotes?status=changes_requested"
          loading={actionCardsLoading}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <ChartCard
          title="Cumulative Revenue"
          data={revenueData}
          timeRange={revenueRange}
          onTimeRangeChange={setRevenueRange}
          loading={revenueLoading}
          chartConfig={revenueChartConfig}
          accentColor="bg-emerald-500"
        />
        <ChartCard
          title="Cumulative Payments"
          data={paymentsData}
          timeRange={paymentsRange}
          onTimeRangeChange={setPaymentsRange}
          loading={paymentsLoading}
          chartConfig={paymentsChartConfig}
          accentColor="bg-blue-500"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <KeyStatsCard
          stats={keyStats}
          timeRange={statsRange}
          onTimeRangeChange={setStatsRange}
          loading={statsLoading}
          isViewOnly={isViewOnly}
        />
        <TopClientsCard
          clients={topClients}
          timeRange={clientsRange}
          onTimeRangeChange={setClientsRange}
          loading={clientsLoading}
        />
      </div>
    </div>
  );
}

export function Dashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentBusiness } = useApp();
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showUltimateOwlModal, setShowUltimateOwlModal] = useState(false);
  const { showUpgradeModal, showTrialExpiredModal, showPaymentFailedModal, showSubscriptionCancelledModal } = useUpgradeModal();

  useEffect(() => {
    if (searchParams.get('welcome') === 'true') {
      setShowWelcomeModal(true);
      // Clear the query param without triggering a re-render
      searchParams.delete('welcome');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleCloseWelcomeModal = () => {
    setShowWelcomeModal(false);
  };

  const handleTestUpgradeModal = (limitType: string) => {
    // Test data - in real usage these come from backend error response
    const testLimits: Record<string, { current: number; max: number }> = {
      max_invoices: { current: 5, max: 5 },
      max_quotes: { current: 3, max: 3 },
      max_businesses: { current: 2, max: 2 },
      max_seats: { current: 3, max: 3 },
    };
    const limits = testLimits[limitType] || { current: 10, max: 10 };

    showUpgradeModal({
      code: 'limit_exceeded',
      reason: 'limit_exceeded',
      limit: limitType,
      message: 'Limit exceeded', // Backend provides this
      current: limits.current,
      max: limits.max,
      upgrade_path: 'premium',
      current_plan: 'base',
    });
  };

  return (
    <>
      <DashboardContent
        onShowWelcomeModal={() => setShowWelcomeModal(true)}
        onShowUpgradeModal={(limitType) => handleTestUpgradeModal(limitType)}
        onShowUltimateOwlModal={() => setShowUltimateOwlModal(true)}
        onShowTrialExpiredModal={showTrialExpiredModal}
        onShowPaymentFailedModal={showPaymentFailedModal}
        onShowSubscriptionCancelledModal={showSubscriptionCancelledModal}
      />
      {showWelcomeModal && currentBusiness?.id && (
        <WelcomeUpgradeModal
          businessId={currentBusiness.id}
          onClose={handleCloseWelcomeModal}
        />
      )}
      {showUltimateOwlModal && currentBusiness?.id && (
        <UltimateOwlUpsellModal
          open={showUltimateOwlModal}
          onClose={() => setShowUltimateOwlModal(false)}
          businessId={currentBusiness.id}
        />
      )}
    </>
  );
}
